import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@ai-tutor/ui';
import { track } from '@/config/observability';
import { queryKeys } from '@/config/query-client';
import { useFocusTimer } from '@/contexts/FocusTimerContext';
import type { FocusSyncStatus } from '@/hooks/focus/useFocusLogSync';
import { useNow } from '@/hooks/ui/useNow';
import { lessonApi } from '@/lib/api-client';
import {
  requestNotifyPermission,
  type FocusSettings,
} from '@/lib/focus/focusAlerts';
import type { DaySummary, FocusLogEntry } from '@/lib/focus/focusLog';
import {
  autoLongBreak,
  autoShortBreak,
  clampFocusMinutes,
  isBetweenRounds,
  remainingMs,
  type FocusSession,
  type FocusState,
  type FocusView,
} from '@/lib/focus/focusMachine';
import { sessionEndsAt } from '@/lib/focus/sessionEnd';
import { toMinutes } from '@/lib/focus/format';
import { useWellbeingKeys } from '../wellbeingStorage';
import type { Worry } from '../worry/WorryDump';
import { FocusRoomView } from './FocusRoomView';
import type { PlanChoice } from './SessionPlanPanel';
import {
  syncWaiting,
  toViewModel,
  type FocusLessonOption,
  type FocusToday,
} from './focusViewModel';
import { useFocusModeShortcut } from './useFocusModeShortcut';
import { useSpaceShortcut } from './useSpaceShortcut';

const PICKER_LIMIT = 100;

/** Recent lessons, offered as one-tap intentions. */
function useFocusLessonOptions(): FocusLessonOption[] {
  const query = useQuery({
    queryKey: queryKeys.lessons.list({ purpose: 'focus-picker' }),
    queryFn: () => lessonApi.getUserLessons({ limit: PICKER_LIMIT }),
    staleTime: 60_000,
  });
  return (query.data?.lessons ?? [])
    .filter(l => l.status !== 'failed')
    .map(l => ({ id: l.lesson_id, title: l.title }));
}

/** Today's line: what this device's log adds up to, and anything unsent. */
function todayLine(today: DaySummary, sync: FocusSyncStatus): FocusToday {
  return {
    blocks: today.blocks,
    minutes: toMinutes(today.focusMs),
    waiting: syncWaiting(sync),
  };
}

/** A finished session, rebuilt from the blocks the log holds for it. */
function summarise(session: FocusSession | null, log: FocusLogEntry[]) {
  if (!session) return null;
  const blocks = log.filter(
    e => e.startedAt >= session.startedAt && e.startedAt <= session.endedAt
  );
  return {
    blocks: session.blocks,
    focusMinutes: toMinutes(blocks.reduce((sum, e) => sum + e.focusMs, 0)),
    parked: session.parked,
    notes: session.notes ?? [],
    rounds: session.rounds,
    recaps: blocks
      .filter(e => e.recap)
      .map(e => ({ id: e.id, intention: e.intention, text: e.recap ?? '' })),
  };
}

/**
 * Parked thoughts into the Worry dump, after what is already there (the dump
 * lists oldest first), so closing the session summary does not lose them.
 */
function useKeepAsWorries() {
  const keys = useWellbeingKeys();
  const [worries, setWorries] = useLocalStorage<Worry[]>(keys.worries, []);
  return (notes: { id: string; text: string }[]) => {
    const now = Date.now();
    const kept = notes.map((n, i) => ({
      id: `parked-${now}-${i}`,
      text: n.text,
      createdAt: now,
    }));
    setWorries([...worries, ...kept]);
  };
}

/** The next session's set-up - what it is for, its plan and its view - before Start. */
function useFocusSetup(state: FocusState) {
  const [intention, setIntention] = useState(state.intention ?? '');
  const [lessonId, setLessonId] = useState(state.lessonId);
  // The last plan is kept (it lives in the timer's state); a break that still
  // matches its auto value is read back as auto.
  const [plan, setPlan] = useState<PlanChoice>(() => {
    const short =
      state.shortMinutes === autoShortBreak(state.focusMinutes)
        ? null
        : state.shortMinutes;
    const autoLong = autoLongBreak(short ?? autoShortBreak(state.focusMinutes));
    return {
      mode: state.mode,
      minutes: state.focusMinutes,
      rounds: state.rounds,
      shortMinutes: short,
      longMinutes: state.longMinutes === autoLong ? null : state.longMinutes,
    };
  });
  // The view is chosen for each session: always Classic at the next set-up.
  const [view, setView] = useState<FocusView>('classic');
  useEffect(() => {
    if (state.lastSession) setView('classic');
  }, [state.lastSession]);
  return {
    intention,
    lessonId,
    plan,
    view,
    onIntentionChange: (text: string, id?: string) => {
      setIntention(text);
      setLessonId(id);
    },
    onPlanChange: (patch: Partial<PlanChoice>) => {
      setPlan(p => ({
        ...p,
        ...patch,
        minutes: clampFocusMinutes(patch.minutes ?? p.minutes),
      }));
      // Flow has no plan to draw: only the classic view.
      if (patch.mode === 'flow') setView('classic');
    },
    onViewChange: setView,
  };
}

/**
 * Focus mode on or off. It follows the block: the setting opens it on every
 * Start (and on a reload mid-block), and it closes once there is no block or
 * break left to watch. Not stored itself - the setting is the preference.
 */
function useFocusMode(state: FocusState, preferred: boolean) {
  const watchable = state.phase !== 'idle' || isBetweenRounds(state);
  const [on, setOn] = useState(() => preferred && watchable);
  useEffect(() => {
    if (!watchable) setOn(false);
  }, [watchable]);
  // A choice the learner made, as opposed to the setting opening it on Start.
  const toggle = (next: boolean) => {
    setOn(next);
    track('focus_mode_toggled', { on: next });
  };
  useFocusModeShortcut(watchable && !on, () => toggle(true));
  return { on, setOn, toggle };
}

/** Space: start, pause, resume - and nothing during a break or a summary. */
function useRoomSpaceShortcut(
  state: FocusState,
  start: () => void,
  summaryShown: boolean,
  dispatch: ReturnType<typeof useFocusTimer>['dispatch']
) {
  useSpaceShortcut(state.phase, {
    // The summary has its moment first: Start waits behind "Plan the next
    // session", so Space does too. Between rounds it starts the next one.
    idle: () => {
      if (summaryShown) return;
      if (isBetweenRounds(state)) dispatch({ type: 'nextBlock' });
      else start();
    },
    focus: () => dispatch({ type: 'pause' }),
    paused: () => dispatch({ type: 'resume' }),
    // A rest is not ended by a stray key: "Back to focus" is a button.
    break: () => {},
  });
}

/** The Focus page, wired to the app-wide timer. */
export function FocusRoom() {
  const timer = useFocusTimer();
  const { state, dispatch, settings, setSettings, today, log, sync } = timer;
  const ticking = state.phase === 'focus' || state.phase === 'break';
  // Idle ticks once a minute: only the plan's "ending around" moves.
  const now = useNow(true, ticking ? 1000 : 60_000);
  const view = toViewModel(state, now);
  const lessons = useFocusLessonOptions();

  const setup = useFocusSetup(state);
  const { intention, lessonId, plan } = setup;

  // Here, the break is the panel beside the timer. The toast's "Take your
  // break" would otherwise open the sheet on top of the very thing it shows.
  const { breakSheetOpen, closeBreakSheet } = timer;
  useEffect(() => {
    if (breakSheetOpen) closeBreakSheet();
  }, [breakSheetOpen, closeBreakSheet]);

  const focusMode = useFocusMode(state, settings.immersive);
  const start = () => {
    dispatch({
      type: 'start',
      mode: plan.mode,
      focusMinutes: plan.minutes,
      rounds: plan.rounds,
      shortMinutes: plan.shortMinutes,
      longMinutes: plan.longMinutes,
      view: plan.mode === 'flow' ? 'classic' : setup.view,
      seed: Date.now(),
      intention,
      lessonId,
    });
    if (settings.immersive) focusMode.setOn(true);
  };

  const summary = useMemo(
    () => summarise(state.lastSession, log),
    [state.lastSession, log]
  );
  useRoomSpaceShortcut(state, start, Boolean(summary), dispatch);
  const left = remainingMs(state, now);
  const keepAsWorries = useKeepAsWorries();

  const onSettingsChange = async (next: FocusSettings) => {
    if (next.notifications && !settings.notifications) {
      next = { ...next, notifications: await requestNotifyPermission() };
    }
    setSettings(next);
  };

  return (
    <FocusRoomView
      view={view}
      setup={{ intention, lessonId, plan, view: setup.view }}
      lessons={lessons}
      today={todayLine(today, sync)}
      now={now}
      endsAt={state.phase === 'focus' && left !== null ? now + left : null}
      sessionEndsAt={sessionEndsAt(state, now)}
      parkedCount={state.parkedCount}
      summary={summary}
      settings={settings}
      onSettingsChange={onSettingsChange}
      immersive={focusMode.on}
      onImmersiveChange={focusMode.toggle}
      onIntentionChange={setup.onIntentionChange}
      onPlanChange={setup.onPlanChange}
      onViewChange={setup.onViewChange}
      onStart={start}
      onPause={() => dispatch({ type: 'pause' })}
      onResume={() => dispatch({ type: 'resume' })}
      onFinish={() => dispatch({ type: 'finish' })}
      onNextBlock={() => dispatch({ type: 'nextBlock' })}
      onEndSession={() => dispatch({ type: 'endSession' })}
      onDismissSummary={() => dispatch({ type: 'dismissSummary' })}
      onKeepNotes={() => {
        keepAsWorries(summary?.notes ?? []);
        dispatch({ type: 'clearSessionNotes' });
      }}
      onAddNote={text => dispatch({ type: 'addNote', text })}
      onRemoveNote={id => dispatch({ type: 'removeNote', id })}
      onSaveRecap={text => dispatch({ type: 'saveRecap', text })}
      onDismissRecap={() => dispatch({ type: 'dismissRecap' })}
    />
  );
}
